import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, Icon, Select, type SelectOption } from '../../../../components';
import { useLanguageSwitcher, languageName } from '../../../../hooks/useLanguageSwitcher';
import { useThemeControls } from '../../../../theme/OwsTheme';
import {
  MODE_SWATCH,
  MODE_SPLIT_LINE,
  THEME_MODE_OPTIONS,
  type ModeSwatch,
  type ThemeMode,
} from '../../../../theme/themeConfig';
import { COLORS } from '../../../../theme/tokens';
import {
  Stack,
  Groups,
  Group,
  GroupHead,
  GroupLabel,
  GroupHint,
  Cards,
  ModeCard,
  PreviewFrame,
  MiniStudio,
  Rail,
  RailDot,
  Main,
  TopBar,
  TitleBar,
  ScoreRing,
  ChartCard,
  ChartLabel,
  Spark,
  KpiRow,
  KpiCard,
  KpiBar,
  SplitSeam,
  ModeLabelRow,
  ModeLabel,
  ModeHint,
  Check,
} from './InterfaceSection.styles';

/** Sparkline geometry for the mock stat card (drawn in the swatch's accent colour). */
const SPARK_LINE = '0,27 17,21 33,24 50,12 68,16 100,4';
const SPARK_AREA = `${SPARK_LINE} 100,36 0,36`;

/**
 * One tiny mock of the studio rendered in a fixed mode palette: side rail (with an
 * active accent item), a top bar (title + score ring), a stat card with a sparkline,
 * and two KPI tiles. Purely decorative, so it's hidden from assistive tech.
 */
const StudioMock = ({ swatch, clip }: { swatch: ModeSwatch; clip?: boolean }): React.ReactElement => (
  <MiniStudio
    aria-hidden="true"
    $bg={swatch.bg}
    $card={swatch.card}
    $rail={swatch.rail}
    $line={swatch.line}
    $text={swatch.text}
    $muted={swatch.muted}
    $accent={swatch.accent}
    $clip={clip}
  >
    <Rail>
      <RailDot $active />
      <RailDot />
      <RailDot />
    </Rail>
    <Main>
      <TopBar>
        <TitleBar />
        <ScoreRing />
      </TopBar>
      <ChartCard>
        <ChartLabel />
        <Spark viewBox="0 0 100 36" preserveAspectRatio="none">
          <polygon points={SPARK_AREA} fill="currentColor" opacity={0.14} />
          <polyline
            points={SPARK_LINE}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </Spark>
      </ChartCard>
      <KpiRow>
        <KpiCard>
          <KpiBar />
        </KpiCard>
        <KpiCard>
          <KpiBar $accent />
        </KpiCard>
      </KpiRow>
    </Main>
  </MiniStudio>
);

/**
 * Settings → Interface. Two live controls: the studio's appearance (Auto / Light /
 * Dark) and the preferred language. Both apply immediately (no Save button): the
 * theme controller persists the choice to the preferences API + localStorage (see
 * `theme/OwsTheme.tsx`), and the language switcher re-translates the UI on pick and
 * saves it to the account (cross-device). The app has a single brand colour
 * (orange), so there is no accent picker.
 */
export const InterfaceSection = (): React.ReactElement => {
  const { t } = useTranslation();
  const { mode, system, setMode } = useThemeControls();

  // Preferred language: options come from the site's configured languages; picking
  // one re-translates the UI right away and persists the choice to the account. The
  // language NAMES (a closed vocabulary from `useLanguageSwitcher`) are translated
  // here at the render site; an unmapped name resolves to itself.
  const { languages, currentLanguage, onChangeLanguage } = useLanguageSwitcher();
  // Endonyms: each option in its own language (never re-translated to the active UI
  // language). The Select renders the label string raw.
  const languageOptions: SelectOption[] = languages.map((code) => ({
    value: code,
    label: languageName(code),
  }));

  // Theme-mode card copy (label + hint) for the closed Auto / Light / Dark set,
  // translated at the render site (the source vocabulary lives in `themeConfig.ts`).
  const themeModeLabel = (value: ThemeMode): string => {
    switch (value) {
      case 'auto':
        return t('Auto');
      case 'light':
        return t('Light');
      case 'dark':
        return t('Dark');
      default:
        return value;
    }
  };
  const themeModeHint = (value: ThemeMode): string => {
    switch (value) {
      case 'auto':
        return t('Match your system appearance');
      case 'light':
        return t('Always use the light theme');
      case 'dark':
        return t('Always use the dark theme');
      default:
        return value;
    }
  };

  const onMode = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const value = event.currentTarget.dataset.id as ThemeMode | undefined;
      if (value) setMode(value);
    },
    [setMode],
  );

  const dark = MODE_SWATCH.dark;
  const light = MODE_SWATCH.light;

  return (
    <Stack>
      <Panel>
        <Groups>
          <Group>
            <GroupHead>
              <GroupLabel text="Theme" tag="span" />
              <GroupHint text="Match your system, or always use light or dark." tag="span" />
            </GroupHead>
            <Cards role="radiogroup" aria-label={t('Theme')}>
              {THEME_MODE_OPTIONS.map((option) => {
                const selected = mode === option.value;
                const label = themeModeLabel(option.value);
                const hint =
                  option.value === 'auto'
                    ? `${themeModeHint(option.value)} · ${system === 'dark' ? t('Dark') : t('Light')}`
                    : themeModeHint(option.value);
                return (
                  <ModeCard
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={t('{{label}} theme', { label })}
                    data-id={option.value}
                    $selected={selected}
                    onClick={onMode}
                  >
                    {option.value === 'auto' ? (
                      <PreviewFrame>
                        <StudioMock swatch={light} />
                        <StudioMock swatch={dark} clip />
                        <SplitSeam viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                          <line
                            x1={44}
                            y1={0}
                            x2={56}
                            y2={100}
                            stroke={MODE_SPLIT_LINE}
                            strokeWidth={1}
                            vectorEffect="non-scaling-stroke"
                          />
                        </SplitSeam>
                      </PreviewFrame>
                    ) : (
                      <PreviewFrame>
                        <StudioMock swatch={MODE_SWATCH[option.value]} />
                      </PreviewFrame>
                    )}
                    <ModeLabelRow>
                      <ModeLabel text={label} tag="span" />
                      {selected ? (
                        <Check>
                          <Icon name="check" size={16} color={COLORS.ctaPillBorder} />
                        </Check>
                      ) : null}
                    </ModeLabelRow>
                    <ModeHint text={hint} tag="span" />
                  </ModeCard>
                );
              })}
            </Cards>
          </Group>
        </Groups>
      </Panel>
      {languageOptions.length > 0 ? (
        <Panel>
          <Group>
            <GroupHead>
              <GroupLabel text="Preferred language" tag="span" />
              <GroupHint text="Your language changes right away and is saved to your account." tag="span" />
            </GroupHead>
            <Select
              label={t('Preferred language')}
              hideLabel
              value={currentLanguage}
              onChange={onChangeLanguage}
              options={languageOptions}
            />
          </Group>
        </Panel>
      ) : null}
    </Stack>
  );
};

export default InterfaceSection;
